import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { DataStatus, Message } from '@wearejh/m2-pwa-engine/lib/types';

import { CartItemDeleteParams, CartItemUpdateParams, CartMsg } from '../cart.actions';
import { AppendState, CartState } from '../cart.reducer';

export interface UseCartItemApi<T extends any = Record<string, any>> {
    readonly isPending: boolean;
    readonly status: DataStatus;
    readonly messages: Message[];
    readonly itemMessages: Message[];
    items: CartState['items'];
    totals: CartState['totals'];
    raw: T | null;
    updateItems(params: CartItemUpdateParams[]);
    deleteItem(params: CartItemDeleteParams);
    clearItemMessages();
}

// const EMPTY_ARR = [];
export const stateSelector = (x: AppendState) => {
    return {
        status: x.cart.status,
        messages: x.cart.messages,
        itemMessages: x.cart.itemMessages,
        items: x.cart.items,
        totals: x.cart.totals,
        raw: x.cart.raw,
    };
};

export function useCartItemApi<T extends any = Record<string, any>>(): UseCartItemApi<T> {
    const dispatch = useDispatch();
    const { items, status, messages, totals, itemMessages, raw } = useSelector(stateSelector, shallowEqual);
    const updateItems = useCallback(
        (items: CartItemUpdateParams[]) => {
            dispatch(CartMsg('Cart.ItemUpdate', items));
        },
        [dispatch],
    );
    const deleteItem = useCallback(
        (params: CartItemDeleteParams) => {
            dispatch(CartMsg('Cart.ItemDelete', params));
        },
        [dispatch],
    );
    const clearItemMessages = useCallback(() => {
        dispatch(CartMsg('Cart.ClearAllItemMessages'));
    }, [dispatch]);
    return useMemo(() => {
        return {
            isPending: status === DataStatus.Pending || status === DataStatus.Idle,
            status,
            updateItems,
            deleteItem,
            items,
            messages,
            itemMessages,
            clearItemMessages,
            totals,
            raw: raw as T,
        };
    }, [clearItemMessages, deleteItem, itemMessages, items, messages, raw, status, totals, updateItems]);
}
